// Two readings of the tree currently on screen, side by side, because they are
// the two halves of one question and answering only one of them misleads.
//
// Left, recall: a small share of leads should carry a large share of
// conversions, which is visible as the Leads bar and the Conversions bar not
// lining up. Right, precision: what a lead in each tier is actually worth.
// A tier can hold most of the conversions only because it holds most of the
// leads, and the pair makes that obvious where either chart alone would not.
//
// Scope is this ONE tree. The heading says so with the tree number rather than
// a caption, because the diagnostics panel sitting beside this on the page
// reports "Across the whole model" and the two scopes have to be told apart at
// a glance.
import React from 'react';
import { observer } from 'mobx-react';
import { Card } from 'react-bootstrap';

import { EnsembleThreshold } from '../../../models/ensembling';
import { TreeLeaf, treeTierPerformance } from './treePerformance';
import {
  pct,
  rate,
  count,
  MIN_INSIDE_LABEL,
  StackedBar,
} from './performanceBands';
import './treePerformanceCharts.css';

function TreePerformanceCharts({
  treeId,
  leaves,
  thresholds,
  open,
  onToggleOpen,
}: {
  treeId: number;
  leaves: TreeLeaf[];
  thresholds?: EnsembleThreshold;
  /**
   * Whether the charts are shown. Closed, the header keeps the totals, which is
   * the one number worth having above the tree at all times. Omitted means always
   * open, so any other caller is unaffected.
   */
  open?: boolean;
  onToggleOpen?: () => void;
}) {
  const performance = treeTierPerformance(leaves, thresholds);
  // No thresholds means no tiers, and a chart drawn anyway would have to invent
  // them. The tree keeps its gradient colouring in that case and this space
  // stays empty.
  if (!performance) return null;

  const { tiers, population, conversions } = performance;
  // A tree with no leads has nothing to say about where its conversions sit.
  // Four empty bands and four zero bars would read as a broken chart rather
  // than as an empty tree, which is the state this actually is.
  if (population <= 0) return null;

  // The tiers arrive best-first (see the fixed-order comment below), so the top two
  // are Very Good and Good. Read off the array rather than matched by name, because
  // a tenant can rename a segment and the ORDER is what carries the meaning.
  // Deliberately no "best tier converts X". It named the TOP tier while
  // printing the highest conversion RATE, and those are only the same tier when
  // the model is well ordered: on a tree whose Medium out-converts its Very
  // Good, the preview credited the rate to the wrong band. The rates are in the
  // precision chart, each against the tier it belongs to. Removed from the Fine
  // tuning card for the same reason.
  const top = tiers.slice(0, 2);
  const headline = top.length
    ? {
        topRecall: top.reduce((sum, tier) => sum + tier.conversionShare, 0),
        topLeads: top.reduce((sum, tier) => sum + tier.leadShare, 0),
        topLabels: top.map((tier) => tier.label).join(' and '),
      }
    : null;

  return (
    <Card className={`tpf-card ${open === false ? 'tpf-card--collapsed' : ''}`}>
      <Card.Body>
        <div className="tpf-head">
          <button
            type="button"
            className="tpf-toggle"
            onClick={onToggleOpen}
            aria-expanded={open !== false}
            disabled={!onToggleOpen}
          >
            <span className="tpf-chevron" aria-hidden="true">
              {open === false ? '►' : '▼'}
            </span>
            <span className="tpf-tree">Tree Performance</span>
            {/* Which tree, beside the name. These curves are computed from ONE
                tree's leaves, which is why the name says Tree and not Model:
                the Fine tuning card by that name reads the whole ensemble, and
                two cards claiming the same scope with different numbers is the
                confusion this avoids. */}
            <span className="tpf-scope">Tree {treeId}</span>
          </button>
          {/* Folded, these numbers ARE the section. Recall is a ratio of two shares
              and needs both halves stated: "76% of conversions" says nothing until
              you know whether that came out of 28% of the leads or 90% of them.
              The best tier's own rate is the second half of the question, because a
              tier can hold most of the conversions purely by holding most of the
              leads. */}
          {open === false && headline && (
            <span className="tpf-preview">
              <span className="tpf-stat">
                {headline.topLabels}: <b>{pct(headline.topLeads)}</b> of leads
                carry <b>{pct(headline.topRecall)}</b> of conversions
              </span>
            </span>
          )}
          <span className="tpf-totals">
            {count(population)} leads &middot; {count(conversions)} conversions
          </span>
        </div>

        <div
          className="tpf-charts"
          hidden={open === false}
          /* `hidden` rather than unmounting: the bars are pure derived state, so
             re-creating them on every open costs a layout pass for nothing, and a
             hidden subtree is skipped by the accessibility tree either way. */
        >
          <section className="tpf-chart">
            <h6 className="tpf-title">Where the conversions come from</h6>
            <StackedBar
              label="Leads"
              tiers={tiers}
              shareOf={(tier) => tier.leadShare}
              describe={(tier) =>
                `${tier.label}: ${count(tier.population)} leads, ${pct(
                  tier.leadShare
                )} of the tree`
              }
            />
            <StackedBar
              label="Conversions"
              tiers={tiers}
              shareOf={(tier) => tier.conversionShare}
              describe={(tier) =>
                `${tier.label}: ${count(tier.conversions)} conversions, ${pct(
                  tier.conversionShare
                )} of the tree`
              }
            />
            {/* One legend for both bars: they are the same four tiers in the
                same order, and a key per bar would say so twice. */}
            <div className="tpf-legend">
              {tiers.map((tier) => (
                <span className="tpf-legend-item" key={tier.segment}>
                  <span
                    className="tpf-legend-dot"
                    style={{ background: tier.color }}
                  />
                  {tier.label}
                </span>
              ))}
            </div>
          </section>

          <section className="tpf-chart">
            <h6 className="tpf-title">How well each tier converts</h6>
            {/* Fixed tier order rather than sorted by length. The tiers are cut
                from the conversion rate itself, so best to worst already is
                longest to shortest; sorting would only ever reorder the rows
                when a tier is empty, and rows that move as you switch trees
                read as a bug. */}
            {tiers.map((tier) => (
              <div className="tpf-rate-row" key={tier.segment}>
                <span className="tpf-rate-label">{tier.label}</span>
                <div className="tpf-rate-track">
                  <span
                    className="tpf-rate-fill"
                    style={{
                      width: `${tier.barPercent}%`,
                      background: tier.color,
                    }}
                  >
                    {tier.barPercent >= MIN_INSIDE_LABEL && (
                      <span
                        className="tpf-rate-value"
                        style={{ color: tier.textColor }}
                      >
                        {rate(tier.conversionRate)}
                      </span>
                    )}
                  </span>
                  {tier.barPercent < MIN_INSIDE_LABEL && (
                    <span className="tpf-rate-value tpf-rate-value--outside">
                      {rate(tier.conversionRate)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </section>
        </div>
      </Card.Body>
    </Card>
  );
}

// Observed so the charts appear once the ensembling configuration lands, the
// same reason the node cards are observed.
export default observer(TreePerformanceCharts);
